import {
  CheckCircleOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  MailOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Divider,
  Form,
  Input,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthenticatedRequest, UserContext } from '../../UserContext';
import { HeroPanel } from '../../components/shared/HeroPanel';
import {
  ConferenceApiError,
  ConferenceReport,
  conferenceRequest,
  isTerminal,
  REPORT_ID_PATTERN,
  reportLink,
  ReportResponse,
  Submission,
} from './api';
import { DeleteReportAction, useRetryWait } from './delete-report';
import { ReportRuns } from './report-runs';
import { ResumeReportAction } from './resume-report';

/** Honor a readable server delay, otherwise back off between 10 and 60 seconds. */
function pollDelay(attempt: number, retry?: number): number {
  return retry ?? Math.min(60_000, 10_000 * 2 ** Math.min(attempt, 3));
}

/** Staff entry point with bookmarkable tabs and existing report detail links. */
export function ConferenceReportPage({
  apiUrl,
}: {
  apiUrl?: string;
}): JSX.Element {
  const user = useContext(UserContext);
  const [params, setParams] = useSearchParams();
  const reportId = params.get('reportId');
  const tab = reportId || params.get('tab') === 'runs' ? 'runs' : 'submit';
  const [initial, setInitial] = useState<ReportResponse>();
  const [deleted, setDeleted] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    if (!reportId || initial?.report.report_id !== reportId) {
      setInitial(undefined);
    }
  }, [reportId, initial]);
  const staff =
    !!user?.accessToken &&
    user.name.toLowerCase().endsWith('@housewhisper.ai') &&
    !user.name.toLowerCase().includes('#ext#');

  return (
    <HeroPanel
      maxWidth={tab === 'runs' && !reportId ? 1280 : 820}
      title="Lead Engagement Audit"
    >
      <Space
        direction="vertical"
        size="large"
        style={{ width: '100%', textAlign: 'left' }}
      >
        {!staff ? (
          <Alert
            message="HouseWhisper staff access is required."
            showIcon
            type="error"
          />
        ) : !apiUrl ? (
          <Alert
            message="The conference report service has not been configured. Contact the Insights administrator."
            showIcon
            type="warning"
          />
        ) : (
          <>
            {deleted && (
              <Alert
                description={<Link to="?tab=submit">Submit report</Link>}
                message="Report deleted. You can submit the account again."
                showIcon
                type="info"
              />
            )}
            {unavailable && (
              <Alert
                message="This report is already unavailable. The report list is being refreshed."
                showIcon
                type="info"
              />
            )}
            <Tabs
              activeKey={tab}
              destroyInactiveTabPane
              items={[
                {
                  key: 'submit',
                  label: 'Submit report',
                  children: tab === 'submit' && (
                    <SubmissionForm
                      apiUrl={apiUrl}
                      key={user?.userID}
                      onReport={response => {
                        setInitial(response);
                        setDeleted(false);
                        setUnavailable(false);
                        setParams({
                          tab: 'runs',
                          reportId: response.report.report_id,
                        });
                      }}
                    />
                  ),
                },
                {
                  key: 'runs',
                  label: 'Report runs',
                  children:
                    tab === 'runs' &&
                    (reportId ? (
                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%' }}
                      >
                        <Link to="?tab=runs">Back to report runs</Link>
                        {!REPORT_ID_PATTERN.test(reportId) ? (
                          <Alert
                            message="The report link contains an invalid report ID."
                            showIcon
                            type="error"
                          />
                        ) : (
                          <ReportStatus
                            apiUrl={apiUrl}
                            initial={
                              initial?.report.report_id === reportId
                                ? initial
                                : undefined
                            }
                            key={`${user?.userID}:${apiUrl}:${reportId}`}
                            onDeleted={() => {
                              setInitial(undefined);
                              setDeleted(true);
                              setUnavailable(false);
                              setParams({ tab: 'runs' }, { replace: true });
                            }}
                            onUnavailable={() => {
                              setInitial(undefined);
                              setUnavailable(true);
                              setParams({ tab: 'runs' }, { replace: true });
                            }}
                            reportId={reportId}
                          />
                        )}
                      </Space>
                    ) : (
                      <ReportRuns
                        apiUrl={apiUrl}
                        key={`${user?.userID}:${apiUrl}`}
                        onDeleted={() => {
                          setDeleted(true);
                          setUnavailable(false);
                        }}
                        onUnavailable={() => setUnavailable(true)}
                      />
                    )),
                },
              ]}
              onChange={value => {
                setInitial(undefined);
                setParams({ tab: value });
              }}
            />
          </>
        )}
      </Space>
    </HeroPanel>
  );
}

/** Keep credentials in the form only, and discard them when it leaves the page. */
function SubmissionForm({
  apiUrl,
  onReport,
}: {
  apiUrl: string;
  onReport: (response: ReportResponse) => void;
}): JSX.Element {
  const authenticatedRequest = useAuthenticatedRequest();
  const [form] = Form.useForm<Submission>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [waiting, wait] = useRetryWait();
  const pending = useRef<AbortController>();
  useEffect(
    () => () => {
      pending.current?.abort();
      form.resetFields();
    },
    [form],
  );

  const submit = async (values: Submission) => {
    if (pending.current || waiting) {
      return;
    }
    const controller = new AbortController();
    pending.current = controller;
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await conferenceRequest(
        apiUrl,
        authenticatedRequest,
        'POST',
        controller.signal,
        undefined,
        {
          email: values.email.trim(),
          api_key: values.api_key.trim(),
        },
      );
      if (!controller.signal.aborted && response) {
        form.resetFields();
        onReport(response);
      }
    } catch (error: unknown) {
      if (!controller.signal.aborted) {
        const failure =
          error instanceof ConferenceApiError
            ? error
            : new ConferenceApiError(0, 'POST');
        setError(failure.message);
        if (failure.retryAfterMs || [409, 503].includes(failure.status)) {
          wait(pollDelay(0, failure.retryAfterMs));
        }
      }
    } finally {
      pending.current = undefined;
      if (!controller.signal.aborted) {
        setSubmitting(false);
      }
    }
  };

  return (
    <Card>
      {error && (
        <Alert
          message={error}
          showIcon
          style={{ marginBottom: 20 }}
          type="error"
        />
      )}
      <Form
        disabled={submitting}
        form={form}
        layout="vertical"
        onFinish={values => void submit(values)}
        preserve={false}
      >
        <Form.Item
          label="Customer email"
          name="email"
          rules={[
            {
              transform: (value: string) => value?.trim(),
              required: true,
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              max: 254,
              message:
                'Enter a valid customer email of at most 254 characters.',
            },
          ]}
        >
          <Input autoComplete="off" type="email" />
        </Form.Item>
        <Form.Item
          label="FUB API key"
          name="api_key"
          rules={[
            {
              transform: (value: string) => value?.trim(),
              required: true,
              max: 1024,
              message:
                'Enter a nonblank FUB API key of at most 1,024 characters.',
            },
          ]}
        >
          <Input.Password autoComplete="off" visibilityToggle={false} />
        </Form.Item>
        <Button
          disabled={waiting}
          htmlType="submit"
          loading={submitting}
          type="primary"
        >
          {waiting ? 'Wait before retrying' : 'Submit report'}
        </Button>
      </Form>
    </Card>
  );
}

/** Poll one request at a time, retaining the ID and last status through outages. */
function ReportStatus({
  apiUrl,
  reportId,
  initial,
  onDeleted,
  onUnavailable,
}: {
  apiUrl: string;
  reportId: string;
  initial?: ReportResponse;
  onDeleted: () => void;
  onUnavailable: () => void;
}): JSX.Element {
  const authenticatedRequest = useAuthenticatedRequest();
  const request = useRef(authenticatedRequest);
  request.current = authenticatedRequest;
  const [report, setReport] = useState<ConferenceReport | undefined>(
    initial?.report,
  );
  const [error, setError] = useState<string>();
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(!initial);
  const [revision, setRevision] = useState(0);
  const [accepted, setAccepted] = useState(initial);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const poll = async () => {
      let delay: number | undefined;
      try {
        const response = await conferenceRequest(
          apiUrl,
          request.current,
          'GET',
          controller.signal,
          reportId,
        );
        if (controller.signal.aborted || !response) {
          return;
        }
        setReport(response.report);
        setError(undefined);
        setPaused(false);
        if (!isTerminal(response.report)) {
          delay = pollDelay(attempts++, response.retryAfterMs);
        }
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          return;
        }
        const failure =
          error instanceof ConferenceApiError
            ? error
            : new ConferenceApiError(0, 'GET');
        setError(failure.message);
        const retryable = ![401, 403, 404, 413, 422].includes(failure.status);
        setPaused(!retryable);
        if (retryable) {
          delay = pollDelay(attempts++, failure.retryAfterMs);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
      if (delay !== undefined && !controller.signal.aborted) {
        timer = setTimeout(() => void poll(), delay);
      }
    };
    if (accepted && revision === 0) {
      if (!isTerminal(accepted.report)) {
        timer = setTimeout(
          () => void poll(),
          pollDelay(0, accepted.retryAfterMs),
        );
      }
    } else {
      setLoading(true);
      void poll();
    }
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [apiUrl, reportId, accepted, revision]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {loading && (
        <div aria-label="Checking report status" role="status">
          <Spin /> Checking report status…
        </div>
      )}
      {error && (
        <Alert
          action={
            paused ? (
              <Button
                disabled={loading}
                onClick={() => setRevision(value => value + 1)}
              >
                Check again
              </Button>
            ) : undefined
          }
          message={error}
          showIcon
          style={{ marginBottom: 20 }}
          type="error"
        />
      )}
      {report && <ReportDetails report={report} />}
      {!report && (
        <Typography.Paragraph>Report ID: {reportId}</Typography.Paragraph>
      )}
      {report && !isTerminal(report) && (
        <Typography.Paragraph type="secondary">
          You can leave this page while we prepare your report. Bookmark it to
          check back later.
        </Typography.Paragraph>
      )}
      {report && (
        <ResumeReportAction
          apiUrl={apiUrl}
          onReport={response => {
            setReport(response.report);
            setAccepted(response);
            setRevision(0);
            setError(undefined);
            setPaused(false);
            setLoading(false);
          }}
          report={report}
        />
      )}
      {report && (
        <Collapse
          expandIcon={({ isActive }) => (
            <RightOutlined aria-hidden rotate={isActive ? 90 : 0} />
          )}
          ghost
          items={[
            {
              key: 'manage',
              label: 'Manage report',
              children: (
                <DeleteReportAction
                  apiUrl={apiUrl}
                  onDeleted={onDeleted}
                  onUnavailable={onUnavailable}
                  report={report}
                />
              ),
            },
          ]}
          size="small"
        />
      )}
    </Space>
  );
}

/** Present backend target and delivery flags without inferring completion time. */
function ReportDetails({ report }: { report: ConferenceReport }): JSX.Element {
  const submitted = Date.parse(report.submitted_at);
  const updated = Date.parse(report.updated_at);
  const delivered = report.status === 'completed' && report.delivery === 'sent';
  const target = Date.parse(report.completion_target || '');
  const link = reportLink(report);
  const completeHistories = report.coverage.complete_histories;
  const plannedHistories = report.coverage.planned_histories;
  const historySummary =
    typeof completeHistories === 'number' &&
    Number.isSafeInteger(completeHistories) &&
    completeHistories >= 0
      ? typeof plannedHistories === 'number' &&
        Number.isSafeInteger(plannedHistories) &&
        plannedHistories >= completeHistories
        ? `${completeHistories.toLocaleString()} of ${plannedHistories.toLocaleString()} requested lead histories included`
        : `${completeHistories.toLocaleString()} requested lead histories included`
      : undefined;
  const status =
    {
      preparing: 'Submitting your request',
      queued: 'Queued',
      running: 'Preparing your report',
      completed: delivered
        ? link
          ? 'Your report is ready'
          : 'Your report is complete'
        : 'Awaiting email confirmation',
      failed: 'We couldn’t finish your report',
      partial: 'Your report is incomplete',
    }[report.status] || 'Report status unavailable';

  return (
    <>
      <Card style={{ textAlign: 'center' }}>
        <div aria-live="polite">
          {link && (
            <Tag color="success" icon={<CheckCircleOutlined aria-hidden />}>
              Ready to view
            </Tag>
          )}
          <Typography.Title level={4}>{status}</Typography.Title>
        </div>
        <Typography.Paragraph strong style={{ marginBottom: 4 }}>
          {report.client_name?.trim() || report.email || report.report_id}
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          Lead Engagement Audit
        </Typography.Paragraph>
        {report.status === 'preparing' && (
          <Typography.Paragraph>
            Your request hasn’t been accepted yet. Waiting for confirmation.
          </Typography.Paragraph>
        )}
        {link && (
          <div style={{ margin: '24px 0 16px' }}>
            <Button
              href={link}
              icon={<ExportOutlined aria-hidden />}
              rel="noopener noreferrer"
              size="large"
              style={{ width: '100%', maxWidth: 220 }}
              target="_blank"
              type="primary"
            >
              View report
            </Button>
          </div>
        )}
        {delivered && (
          <Typography.Paragraph type="secondary">
            <MailOutlined aria-hidden /> Report email sent
          </Typography.Paragraph>
        )}
        {delivered && !link && (
          <Alert
            message="The report link is unavailable. Contact the Insights administrator for help opening it."
            showIcon
            type="warning"
          />
        )}
        {(Number.isFinite(submitted) || historySummary) && (
          <>
            <Divider style={{ margin: '24px 0 16px' }} />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                justifyContent: 'space-between',
              }}
            >
              {Number.isFinite(submitted) && (
                <Typography.Text type="secondary">
                  Requested {new Date(submitted).toLocaleDateString()}
                </Typography.Text>
              )}
              {historySummary && (
                <Typography.Text type="secondary">
                  {historySummary}
                </Typography.Text>
              )}
            </div>
          </>
        )}
      </Card>
      {delivered && report.completed_late && (
        <Alert
          message="Completed late. The report was completed and emailed after its original 12-hour target."
          showIcon
          style={{ margin: '16px 0' }}
          type="success"
        />
      )}
      {!delivered && report.target_missed && (
        <Alert
          message="The 12-hour completion target was missed. Complete reports can still be delivered after this target."
          showIcon
          style={{ margin: '16px 0' }}
          type="warning"
        />
      )}
      <Typography.Paragraph style={{ margin: '16px 0' }} type="secondary">
        <InfoCircleOutlined aria-hidden /> Based on available CRM records. Some
        activity may be missing.
      </Typography.Paragraph>
      <Collapse
        expandIcon={({ isActive }) => (
          <RightOutlined aria-hidden rotate={isActive ? 90 : 0} />
        )}
        ghost
        items={[
          {
            key: 'scope',
            label: 'What’s included in this report?',
            children: (
              <>
                <Typography.Paragraph>
                  {historySummary ? `${historySummary}. ` : ''}
                  Only activity available through your CRM connection can be
                  included. Private, hidden, or unavailable activity may be
                  missing, even when all requested lead histories are included.
                </Typography.Paragraph>
                {report.limitations.length > 0 && (
                  <>
                    <Typography.Title level={5}>
                      Detailed methodology
                    </Typography.Title>
                    <ul>
                      {report.limitations.map((limitation, index) => (
                        <li key={index}>{limitation}</li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            ),
          },
          {
            key: 'support',
            label: 'Details for support',
            children: (
              <>
                <Descriptions column={1} style={{ overflowWrap: 'anywhere' }}>
                  <Descriptions.Item label="Customer email">
                    {report.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Report ID">
                    {report.report_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Submitted">
                    {Number.isFinite(submitted)
                      ? new Date(submitted).toLocaleString()
                      : 'Unavailable'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last updated">
                    {Number.isFinite(updated)
                      ? new Date(updated).toLocaleString()
                      : 'Unavailable'}
                  </Descriptions.Item>
                  {Number.isFinite(target) && (
                    <Descriptions.Item label="Completion target">
                      {new Date(target).toLocaleString()}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Status">
                    {report.status}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email delivery">
                    {report.delivery || 'Not yet confirmed'}
                  </Descriptions.Item>
                </Descriptions>
                {Object.keys(report.coverage).length > 0 && (
                  <>
                    <Typography.Title level={5}>Coverage data</Typography.Title>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {JSON.stringify(report.coverage, null, 2)}
                    </pre>
                  </>
                )}
              </>
            ),
          },
        ]}
        size="small"
      />
    </>
  );
}
